import React, { useEffect, useState } from 'react';
import {
  TextField,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import * as partnerApi from '../../../../lib/api/partner';

/**
 * 외주 업체 선택 컴포넌트
 * PartnerSelectbox와 달리 선택 시 partnerUuid + partnerNm 동시 반환
 *
 * @param {string} uuidName   - formik field명 (partnerUuid 저장)
 * @param {string} nmName     - formik field명 (partnerNm 저장)
 * @param {object} formik     - formik instance
 * @param {string} label      - 라벨
 * @param {string} variant    - outlined | filled | standard
 * @param {string} size       - small | medium
 * @param {boolean} required
 */
const OutsourcePartnerSelectbox = ({
  formik,
  uuidName = 'partnerUuid',
  nmName = 'partnerNm',
  label = '외주업체 선택',
  variant = 'outlined',
  size = 'small',
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [defaultValue, setDefaultValue] = useState(null);
  const loading = options.length === 0;

  // 업체 목록 로드
  useEffect(() => {
    let active = true;
    if (!loading) return undefined;

    (async () => {
      try {
        const response = await partnerApi.getPartners();
        const list = _.uniqBy(response.data.list, 'partnerNm');
        if (active) setOptions(list);
      } catch (err) {
        console.error('외주업체 목록 조회 실패:', err);
      }
    })();

    return () => { active = false; };
  }, []);

  // 수정 모드: 기존 값으로 defaultValue 세팅
  useEffect(() => {
    if (options.length > 0 && formik.values[uuidName]) {
      const selected = options.find(item => item.partnerUuid === formik.values[uuidName]);
      setDefaultValue(selected || null);
    }
  }, [options, formik.values[uuidName]]);

  const handleChange = (event, newValue) => {
    if (newValue) {
      setDefaultValue(newValue);
      formik.setFieldValue(uuidName, newValue.partnerUuid);
      formik.setFieldValue(nmName, newValue.partnerNm);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    if (!newInputValue) {
      formik.setFieldValue(uuidName, '');
      formik.setFieldValue(nmName, '');
      setDefaultValue(null);
    }
  };

  return (
    <Autocomplete
      loading={loading}
      id={uuidName}
      options={options}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={defaultValue}
      freeSolo
      selectOnFocus
      clearOnBlur
      autoHighlight
      getOptionLabel={(option) =>
        typeof option === 'string' ? '' : `(${option.partnerTyp}) ${option.partnerNm}`
      }
      isOptionEqualToValue={(option, value) => option.partnerUuid === value?.partnerUuid}
      renderOption={(props, option) => (
        <Box
          key={option.partnerUuid}
          component="li"
          sx={{ display: 'flex', alignItems: 'center', p: 1 }}
          {...props}
        >
          <Typography display="inline" variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            ({option.partnerTyp})
          </Typography>
          <Typography display="inline" variant="body1">
            {option.partnerNm}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          variant={variant}
          size={size}
          required={required}
          error={formik.touched[uuidName] && Boolean(formik.errors[uuidName])}
          helperText={formik.touched[uuidName] && formik.errors[uuidName]}
          InputProps={{
            ...params.InputProps,
            autoComplete: 'new-password',
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default OutsourcePartnerSelectbox;
